import React from 'react'
import styled from 'styled-components/macro'
import { StockSurfaceCard } from 'src/components/stock/StockPageChrome'
import {
  FOOD_SAFETY_SECONDARY_TEXT,
  FOOD_SAFETY_SURFACE,
} from 'src/constants/foodSafety'
import {
  WASTAGE_COPY,
  wastageAboutCost,
  wastageDisplayUnit,
  wastageEntryDateLabel,
  wastageLastEditedLine,
  wastageRecordedByLine,
  wastageSnapshotIngredientLabel,
  wastageStockTakeLine,
} from 'src/constants/wastage'

const quantityLabel = entry => {
  if (entry?.inputQty == null || entry.inputQty === '') return ''
  const unit = wastageDisplayUnit(entry.inputQty, entry.inputUnit)
  return unit ? `${entry.inputQty} ${unit}` : String(entry.inputQty)
}

const detailLine = (entry, today) =>
  [
    entry.reasonLabel,
    wastageEntryDateLabel(entry.businessDate, today),
    wastageAboutCost(entry.costValuePence),
  ]
    .filter(Boolean)
    .join(' · ')

export const WasteHistory = ({
  entries,
  today,
  editingId = '',
  onEdit,
  onRemove,
  onAgain,
}) => {
  if (!entries?.length) return null

  return (
    <HistoryCard data-testid='waste-history'>
      {entries.map((entry, index) => {
        const supplier = String(entry.supplierTitle || '').trim()
        const ingredients = Array.isArray(entry.recipeSnapshot?.ingredients)
          ? entry.recipeSnapshot.ingredients.map(wastageSnapshotIngredientLabel)
          : []
        const quantity = quantityLabel(entry)
        const detail = detailLine(entry, today)
        const recorded = wastageRecordedByLine(entry)
        const edited = wastageLastEditedLine(entry)
        const note = String(entry.note || '').trim()
        const id = entry._id || entry.id || index
        const removed = Boolean(entry.deletedAt)
        return (
          <Row
            key={id}
            data-testid={`waste-entry-${id}`}
            data-editing={String(editingId) === String(id) ? 'true' : 'false'}
            data-removed={removed ? 'true' : 'false'}
          >
            <Main>
              <Title data-removed={removed ? 'true' : 'false'}>
                {entry.title}
              </Title>
              {supplier ? (
                <Supplier data-testid='waste-entry-supplier'>
                  {supplier}
                </Supplier>
              ) : null}
              {ingredients.length ? (
                <Ingredients data-testid='waste-entry-ingredients'>
                  {ingredients.map((line, index) => (
                    <Ingredient key={`${line}-${index}`}>{line}</Ingredient>
                  ))}
                </Ingredients>
              ) : null}
              {removed ? (
                <Removed data-testid='waste-entry-removed'>
                  {WASTAGE_COPY.removed}
                </Removed>
              ) : null}
              {detail ? <Detail>{detail}</Detail> : null}
              {recorded ? (
                <Detail data-testid='waste-entry-recorded'>{recorded}</Detail>
              ) : null}
              {edited ? (
                <Detail data-testid='waste-entry-edited'>{edited}</Detail>
              ) : null}
              {note ? (
                <Detail data-testid='waste-entry-note'>{note}</Detail>
              ) : null}
              {entry.outsideRange ? (
                <Detail data-testid='waste-entry-outside'>
                  {WASTAGE_COPY.outsideDates}
                </Detail>
              ) : null}
              <Take data-testid='waste-entry-stock-take'>
                {wastageStockTakeLine(
                  entry.stockTake,
                  entry.openStockTake,
                  entry.businessDate,
                )}
              </Take>
            </Main>
            <Side>
              {quantity ? <Quantity>{quantity}</Quantity> : null}
              {!removed && onEdit ? (
                <QuietButton
                  type='button'
                  data-testid={`waste-edit-${id}`}
                  onClick={() => onEdit(entry)}
                >
                  {WASTAGE_COPY.edit}
                </QuietButton>
              ) : null}
              {!removed && onAgain ? (
                <QuietButton
                  type='button'
                  data-testid={`waste-again-${id}`}
                  onClick={() => onAgain(entry)}
                >
                  {WASTAGE_COPY.recordAgain}
                </QuietButton>
              ) : null}
              {!removed && onRemove ? (
                <QuietButton
                  type='button'
                  data-testid={`waste-remove-${id}`}
                  onClick={() => onRemove(entry)}
                >
                  {WASTAGE_COPY.remove}
                </QuietButton>
              ) : null}
            </Side>
          </Row>
        )
      })}
    </HistoryCard>
  )
}

const HistoryCard = styled(StockSurfaceCard)`
  display: flex;
  flex-direction: column;
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  padding: 0;
`

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1rem;

  & + & {
    border-top: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  }
`

const Main = styled.div`
  min-width: 0;
`

const Title = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colours.bbqBlack || '#000'};

  &[data-removed='true'] {
    color: ${FOOD_SAFETY_SECONDARY_TEXT};
    text-decoration: line-through;
  }
`

const Removed = styled.div`
  margin-top: 0.35rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  font-size: 0.9rem;
`

const Supplier = styled.div`
  margin-top: 0.15rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  font-size: 0.9rem;
`

const Ingredients = styled.ul`
  margin: 0.25rem 0 0;
  padding: 0;
  list-style: none;
`

const Ingredient = styled.li`
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  font-size: 0.9rem;
`

const Detail = styled.div`
  margin-top: 0.35rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  font-size: 0.9rem;
`

const Take = styled.div`
  margin-top: 0.35rem;
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  font-size: 0.9rem;
`

const Side = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
  flex: 0 0 auto;
`

const Quantity = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colours.bbqBlack || '#000'};
`

const QuietButton = styled.button`
  margin: 0;
  padding: 0.2rem 0.55rem;
  border: 1.5px solid ${({ theme }) => theme.colours.panSilver || '#e0dfdf'};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
`

export default WasteHistory
